import { DragEvent } from '@interactjs/types';
import * as bootstrap from 'bootstrap';
import interact from 'interactjs';
import { GetBootstrapPrefix, GetBootstrapVersion } from './bootstrap';
import * as errors from './errors';
import { ReportValidity } from './form';
import { IsCallback } from './functions';
import * as html from './html';
import * as object from './object';
import { MaybePromise } from './promise';
import { UUID } from './uuid';

export interface Button {
    text: string;
    class: string;
    click: (e: MouseEvent) => void;
}

export class ModalHideError extends Error {
    constructor(message: string = 'closed') {
        super(message);
        this.name = 'ModalCloseError';
        // Set the prototype explicitly to maintain proper prototype chain
        Object.setPrototypeOf(this, new.target.prototype);
    }
}

export interface Options<T = void> {
    title?: string;
    headerLevel?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
    appendModalClass?: string | undefined;
    close?: false | string;
    outsideClosable?: false;
    preSubmit?: () => MaybePromise<unknown>;
    onSubmit?: () => MaybePromise<T>;
    confirmText?: string;
    confirmHTML?: string;
    cancelText?: string;
    cancelButton?: false;
    body?: string | HTMLElement | Node;
    fullScreenLoader?: string | true;
    text?: string;
    size?: 'modal-lg' | 'modal-xl' | 'modal-sm' | 'modal-fullscreen';
    width?: string;
    appendBodyClass?: string;
    escape?: boolean;
    buttons?: Button[];
    onClose?: () => void;
    appendButtonClass?: string;
    appendConfirmButtonClass?: string;
    appendCancelButtonClass?: string;
    appendCloseButtonClass?: string;
    confirmButtonClass?: string;
    cancelButtonClass?: string;
    closeButtonClass?: string;
    animation?: boolean;
    submitAutoFocus?: boolean;
    reverseButtonOrder?: boolean;
    modalDialogScrollable?: boolean;
    input?: 'text' | 'password' | 'email' | 'number' | 'tel' | 'url' | 'date' | 'time' | 'datetime-local' | 'month' | 'week' | 'color' | 'range' | 'textarea' | HTMLInputElement;
    inputPlaceholder?: string;
    value?: string;
    appendModalContentClass?: string;
    disableErrScroll?: boolean;
    init?: (modal: Modal<unknown>) => void;
    appendClassBySelector?: Record<string, string>;
    draggable?: boolean;
    toggleFullscreen?: boolean;
    allowClickBehind?: boolean;
}

let defaults: Omit<Options, 'onSubmit'> | undefined;

export function SetDefaults(opts: Options) {
    defaults = opts;
}

let startingZIndex: number | undefined;
let currentZIndex: number | undefined;

function resetZIndexes() {
    const $modals = Array.from(document.querySelectorAll<HTMLElement>('.modal'));
    if (!$modals.length) {
        return;
    }

    $modals.sort((a, b) => {
        const aZ = parseInt(getComputedStyle(a).zIndex, 10);
        const bZ = parseInt(getComputedStyle(b).zIndex, 10);
        return aZ - bZ;
    });

    if (startingZIndex === undefined) {
        startingZIndex = parseInt(getComputedStyle($modals[0]).zIndex, 10);
    }

    currentZIndex = startingZIndex;
    for (const $modal of $modals) {
        $modal.style.zIndex = currentZIndex.toString();
        currentZIndex++;
    }
}

export default class Modal<T = void> {
    Bootstrap: bootstrap.Modal;
    Element: HTMLElement;
    Promise: Promise<T>;
    private resolve: (value: T) => void = () => undefined;
    private reject: (reason?: unknown) => void = () => undefined;

    private modalSuccess: HTMLElement;
    private modalError: HTMLElement;
    private modalWarning: HTMLElement;

    private resolveClosed: () => void = () => undefined;
    Closed: boolean = false;
    ClosedPromise: Promise<void>;

    constructor(opts?: Options<T>) {
        if (defaults) {
            if (opts) {
                opts = { ...defaults, ...opts };
            } else {
                opts = defaults;
            }
        }

        this.Promise = new Promise<T>((resolve, reject) => {
            this.resolve = resolve;
            this.reject = reject;
        });

        this.ClosedPromise = new Promise<void>(resolve => {
            this.resolveClosed = resolve;
        });

        const uuid = UUID();

        const div = html.FromString(`
        <div class="modal ${opts?.appendModalClass ?? ''} ${opts?.allowClickBehind ? 'pe-none' : ''}">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content ${opts?.appendModalContentClass ?? ''} ${opts?.allowClickBehind ? 'pe-auto' : ''}">
                    <div class="modal-header">
                        <${opts?.headerLevel ?? 'h5'} class="modal-title"></${opts?.headerLevel ?? 'h5'}>
                        ${GetBootstrapVersion() === 4 ? `<button type="button" class="${opts?.closeButtonClass ?? `close ${opts?.appendCloseButtonClass || ''}`}" data-dismiss="modal" aria-label="Close">
                            <span aria-hidden="true">&times;</span>
                        </button>` : `
                        <button type="button" class="${opts?.closeButtonClass ?? `btn-close ${opts?.appendCloseButtonClass || ''}`}" data-bs-dismiss="modal" aria-label="Close"></button>`}
                    </div>
                    <div class="modal-body ${opts?.appendBodyClass ?? ''}">
                        <form id="form-${uuid}">
                            <fieldset>
                            </fieldset>
                        </form>
                    </div>
                    <div class="modal-error ${GetBootstrapVersion() === 4 ? 'p-1' : 'p-3'} bg-danger text-white text-center text-break d-none"></div>
                    <div class="modal-warning ${GetBootstrapVersion() === 4 ? 'p-1' : 'p-3'} bg-warning text-white text-center text-break d-none"></div>
                    <div class="modal-success ${GetBootstrapVersion() === 4 ? 'p-1' : 'p-3'} bg-success text-white text-center text-break d-none"></div>
                    <div class="modal-footer">
                        <button id="modal-close-btn" form="form-${uuid}" type="button" class="${opts?.cancelButtonClass ?? `btn btn-secondary ${opts?.appendButtonClass || ''} ${opts?.appendCancelButtonClass || ''}`}" ${GetBootstrapVersion() === 4 ? 'data-dismiss' : 'data-bs-dismiss'}="modal">${html.Escape(opts?.cancelText || 'Cancel')}</button>
                        <button id="modal-confirm-btn" type="submit" form="form-${uuid}"  class="${opts?.confirmButtonClass ?? `btn btn-primary ${opts?.appendButtonClass || ''} ${opts?.appendConfirmButtonClass || ''}`}">${opts?.confirmHTML || html.Escape(opts?.confirmText || 'Submit')}</button>
                    </div>
                </div>
            </div>
        </div>`);

        div.addEventListener('mousedown', () => {
            if (dialog.classList.contains('modal-fullscreen')) {
                return;
            }

            this.BringToFront();
        });

        if (opts?.appendClassBySelector) {
            for (const selector of Object.keys(opts?.appendClassBySelector)) {
                for (const $ of Array.from(div.querySelectorAll(selector))) {
                    $.classList.add(...opts.appendClassBySelector[selector].split(' '));
                }
            }
        }

        if (opts?.width) {
            div.style.setProperty(`--${GetBootstrapPrefix()}modal-width`, opts.width);
        }

        if (opts?.animation) {
            div.classList.add('fade');
        }

        const dialog = div.querySelector('.modal-dialog') as HTMLElement;

        if (opts?.modalDialogScrollable) {
            dialog.classList.add('modal-dialog-scrollable');
        }

        const $content = div.querySelector<HTMLElement>('.modal-content') as HTMLElement;
        const header = div.querySelector<HTMLElement>('.modal-header');
        const title = div.querySelector<HTMLElement>('.modal-title');
        const closeLink = div.querySelector<HTMLElement>('.btn-close');

        const body = div.querySelector<HTMLElement>('.modal-body fieldset') as HTMLFieldSetElement;
        const form = div.querySelector('.modal-body form') as HTMLFormElement;
        const footer = div.querySelector<HTMLElement>('.modal-footer');
        this.modalError = div.querySelector<HTMLElement>('.modal-error') as HTMLElement;
        const modalSuccess = div.querySelector<HTMLElement>('.modal-success');
        this.modalWarning = div.querySelector<HTMLElement>('.modal-warning') as HTMLElement;

        const closeButton = div.querySelector<HTMLElement>('#modal-close-btn');
        const confirmButton = div.querySelector<HTMLElement>('#modal-confirm-btn');

        if (opts?.reverseButtonOrder && footer && closeButton) {
            footer.append(closeButton);
        }

        const offset = { x: 0, y: 0 };
        if (opts?.draggable && header) {
            header.classList.add('user-select-none');

            const mousePos = { x: 0, y: 0 };

            interact(header).draggable({
                listeners: {
                    start(event: DragEvent) {
                        div.classList.add('overflow-hidden');

                        mousePos.x = event.clientX;
                        mousePos.y = event.clientY;
                    },
                    end() {
                        div.classList.remove('overflow-hidden');
                    },
                    move(event: DragEvent) {
                        if (dialog.classList.contains('modal-fullscreen')) {
                            return;
                        }

                        const clientX = Math.min(Math.max(event.clientX, 0), window.innerWidth);
                        const clientY = Math.min(Math.max(event.clientY, 0), window.innerHeight);

                        const dx = clientX - mousePos.x;
                        const dy = clientY - mousePos.y;
                        mousePos.x = clientX;
                        mousePos.y = clientY;
                        offset.x += dx;
                        offset.y += dy;

                        $content.style.setProperty('transform', `translate(${offset.x}px, ${offset.y}px)`);
                    },
                },
            }).styleCursor(false);
        }

        if (opts?.toggleFullscreen && header) {
            header.classList.add('user-select-none');

            header.addEventListener('dblclick', () => {
                if (dialog.classList.contains('modal-fullscreen')) {
                    dialog.classList.remove('modal-fullscreen');
                    $content.style.setProperty('transform', `translate(${offset.x}px, ${offset.y}px)`);
                    if (opts.size) {
                        dialog.classList.add(opts.size);
                    }
                } else {
                    dialog.classList.add('modal-fullscreen');
                    $content.style.setProperty('transform', '');
                    if (opts.size) {
                        dialog.classList.remove(opts.size);
                    }
                }
            });
        }

        if (title) {
            if (opts?.title) {
                title.textContent = opts.title;
            } else {
                title.parentElement?.removeChild(title);
            }
        }

        if (opts?.close === false) {
            closeLink?.parentElement?.removeChild(closeLink);
            closeButton?.parentElement?.removeChild(closeButton);
        } else if (opts?.close) {
            if (closeButton) {
                closeButton.textContent = opts.close;
            }
        }

        if (opts?.cancelButton === false) {
            closeButton?.parentElement?.removeChild(closeButton);
        }

        if (!opts?.title) {
            header?.parentElement?.removeChild(header);
        }

        if (opts?.fullScreenLoader) {
            opts.outsideClosable ??= false;
        }

        if (opts?.outsideClosable === false) {
            if (GetBootstrapVersion() === 4) {
                div.setAttribute('data-backdrop', 'static');
                setTimeout(() => {
                    const backdrop = div.previousElementSibling as HTMLElement;
                    backdrop.addEventListener('click', () => false);
                });
            } else {
                div.setAttribute('data-bs-backdrop', 'static');
            }
        }

        if (typeof opts?.body === 'string') {
            body.innerHTML = opts.body;
        } else if (opts?.body) {
            body.appendChild(opts.body);
        }

        if (opts?.input) {
            const $inputParent = document.createElement('div');
            $inputParent.classList.add('mb-3');

            if (opts.text) {
                const $label = document.createElement('label');
                $label.setAttribute('for', 'modal-input');
                $label.classList.add('form-label');
                $label.textContent = opts.text;
                $inputParent.appendChild($label);
            }

            let $input: HTMLElement;
            if (opts.input instanceof HTMLInputElement) {
                $input = opts.input;
            } else {
                if (opts.input === 'textarea') {
                    $input = document.createElement('textarea');
                    $input.setAttribute('rows', '3');
                } else {
                    $input = document.createElement('input');
                    $input.setAttribute('type', opts.input);
                }
                $input.classList.add('form-control');
                if (opts.inputPlaceholder) {
                    $input.setAttribute('placeholder', opts.inputPlaceholder);
                }
            }
            $input.id = 'modal-input';
            $inputParent.appendChild($input);

            body.appendChild($inputParent);

            const isValuer = (v: unknown): v is { value: string } => object.IsObject(v) && object.HasKey(v, 'value') && typeof v.value === 'string';

            opts.onSubmit ??= () => {
                if (isValuer($input)) {
                    return $input.value as unknown as T;
                }
                return undefined as unknown as T;
            };
            opts.submitAutoFocus ??= false;

            if (opts.value && isValuer($input)) {
                $input.value = opts.value;
            }

            setTimeout(() => {
                $input.focus();
                div.scrollTo(0, 0);
            });
        } else if (opts?.text) {
            body.innerHTML = opts.text;
        }

        if (!opts?.onSubmit && !opts?.confirmText) {
            confirmButton?.parentElement?.removeChild(confirmButton);
        } else if (opts.submitAutoFocus ?? true) {
            setTimeout(() => {
                confirmButton?.focus();
                div.scrollTo(0, 0);
            });
        }

        if (opts?.size) {
            dialog?.classList.add(opts.size);
        }

        if (opts?.buttons?.length) {
            for (const btn of opts.buttons.reverse()) {
                const b = document.createElement('button');
                b.classList.add(...btn.class.split(' '));
                b.textContent = btn.text;
                b.type = 'button';
                b.addEventListener('click', e => btn.click(e));

                footer?.insertBefore(b, footer.firstChild);
            }
        }

        if (form) {
            form.noValidate = true;
            form.addEventListener('submit', async e => {
                e.preventDefault();

                const $footerButtons = Array.from(footer?.querySelectorAll('button') ?? []);

                body?.setAttribute('disabled', 'disabled');
                $footerButtons.forEach(b => b.setAttribute('disabled', 'disabled'));

                const spinner = html.FromString(`
                <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>`);
                confirmButton?.insertBefore(spinner, confirmButton.firstChild);

                this.HideError();

                if (opts?.preSubmit) {
                    await opts.preSubmit();
                }
                body?.removeAttribute('disabled');
                $footerButtons.forEach(b => b.removeAttribute('disabled'));
                if (!ReportValidity(form)) {
                    spinner.parentElement?.removeChild(spinner);
                    return false;
                }
                body?.setAttribute('disabled', 'disabled');
                $footerButtons.forEach(b => b.setAttribute('disabled', 'disabled'));

                try {
                    if (opts?.onSubmit) {
                        const submit = opts.onSubmit;
                        this.resolve(await new Promise(resolve => setTimeout(() => resolve(submit()), 4)));
                    } else {
                        this.resolve(undefined as unknown as T);
                    }
                    this.Close();
                } catch (err: unknown) {
                    if (!opts?.disableErrScroll) {
                        div.scrollTo(0, div.scrollHeight);
                    }
                    body?.removeAttribute('disabled');
                    $footerButtons.forEach(b => b.removeAttribute('disabled'));
                    spinner.parentElement?.removeChild(spinner);

                    this.reject(err);
                    if (err instanceof ModalHideError) {
                        return;
                    }
                    errors.HandleRequestError(err, form, err => this.ShowError(err));
                }
            });
        }

        const bsOpts: Partial<bootstrap.Modal.Options> = {
            keyboard: false,
            focus: false,
        };
        if (opts?.allowClickBehind) {
            bsOpts.backdrop = false;
        }
        const bs = new bootstrap.Modal(div, bsOpts);
        if (!modalSuccess) {
            throw new Error('modalSuccess is null');
        }

        this.Bootstrap = bs;
        this.Element = div;
        this.modalSuccess = modalSuccess;

        if (typeof opts?.escape === 'undefined' || opts?.escape) {
            const esc = (e: KeyboardEvent) => {
                if (e.key === 'Escape') {
                    bs.hide();
                }
            };
            document.addEventListener('keyup', esc);

            div.addEventListener('hidden.bs.modal', () => document.removeEventListener('keyup', esc));
        }

        div.addEventListener('hidden.bs.modal', () => {
            this.Close();
        });

        bs.show();

        this.BringToFront();

        // hide all tooltips
        for (const e of Array.from(document.querySelectorAll('[aria-describedby*="tooltip"]'))) {
            if (IsCallback(bootstrap.Tooltip['getInstance'])) {
                bootstrap.Tooltip.getInstance(e)?.hide();
            }
        }

        if (opts?.fullScreenLoader) {
            dialog?.parentElement?.removeChild(dialog);

            div.classList.add('d-flex', 'justify-content-center', 'align-items-center', 'flex-column');

            const loading = `
            <h1 class="text-white">${html.Escape(typeof opts.fullScreenLoader === 'string' ? opts.fullScreenLoader : 'Loading...')}</h1>
            <div class="spinner-border text-white" style="width:3rem;height:3rem" role="status">
                <span class="visually-hidden">Loading...</span>
            </div>`;

            div.innerHTML = loading;
        }

        if (opts?.onClose) {
            div.addEventListener('hide.bs.modal', () => {
                if (opts?.onClose) {
                    opts.onClose();
                }
            });
        }

        opts?.init?.(this as Modal<unknown>);
    }

    Close() {
        if (this.Closed) {
            return;
        }

        if (GetBootstrapVersion() === 4) {
            $(this.Element).modal('dispose');
        } else {
            this.Bootstrap.dispose();
        }
        const maybeBackdrop = this.Element.previousElementSibling;
        if (maybeBackdrop?.classList.contains('modal-backdrop')) {
            maybeBackdrop.remove();
        }
        this.Element.remove();

        // fix body overflow
        document.body.classList.remove('modal-open');
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
        document.body.removeAttribute('data-bs-overflow');
        document.body.removeAttribute('data-bs-padding-right');

        this.resolveClosed();
        this.Closed = true;
    }

    ShowSuccess(s: string) {
        this.HideError();
        this.HideWarning();

        this.modalSuccess.textContent = s;
        this.modalSuccess.classList.remove('d-none');
    }

    HideSuccess() {
        this.modalSuccess.classList.add('d-none');
    }

    ShowError(err: string) {
        this.HideSuccess();
        this.HideWarning();

        this.modalError.innerHTML = html.Nl2br(html.Escape(err));
        this.modalError.classList.remove('d-none');
    }

    HideError() {
        this.modalError.classList.add('d-none');
    }

    ShowWarning(err: string) {
        this.HideSuccess();
        this.HideError();

        this.modalWarning.innerHTML = html.Nl2br(html.Escape(err));
        this.modalWarning.classList.remove('d-none');
    }

    HideWarning() {
        this.modalWarning.classList.add('d-none');
    }

    BringToFront = () => {
        if (startingZIndex === undefined) {
            startingZIndex = parseInt(getComputedStyle(this.Element).zIndex, 10);
        }
        if (currentZIndex === undefined) {
            currentZIndex = startingZIndex;
        }
        currentZIndex++;
        this.Element.style.zIndex = currentZIndex.toString();

        resetZIndexes();
    };
}
